// =========================================================================
// Projections for a module hub page. Route `/workflows/modules/:moduleId`.
// Demo `renderModule`, source line 3011.
//
// Transformation layer — pure functions of explicit arguments. No React,
// no data fetching, no side effects.
// Fork-owned (zone C), P5a lane.
// =========================================================================

import { AREAS, AREA_COVERAGE_LABEL, type Area } from '@/versions/v1/shared/seed/areas';
import { AGENT_BY_NAME, type OemAgent } from '@/versions/v1/shared/seed/agents';
import { CONNECTORS, type OemConnector } from '@/versions/v1/shared/seed/connectors';
import {
  AGENT_COLOURS,
  MODULES,
  MODULE_BY_ID,
  MODULE_STATUS_TOKENS,
  STAGE_TO_MODULE,
  type LegacyStage,
  type ModuleId,
  type OemModule,
} from '@/versions/v1/shared/seed/modules';
import { workflowStepsForModule, type WorkflowStep } from '@/versions/v1/shared/seed/workflow-steps';

/**
 * Resolve a route segment to a module.
 *
 * The demo accepted three things here (`MOD_BY[id] || MOD_BY[STAGE2MOD[id]] ||
 * MOD_BY[MOD_CUR]`): a module id, one of the seven legacy stage ids it used to
 * route by, or nothing at all — in which case it fell back to whichever module
 * was last open. The first two are kept, because old links and the legacy
 * stage vocabulary both still exist. The third is not: "last open" is
 * render-order state, and a URL that renders a different page depending on
 * where the reader came from is not a URL. An unknown id returns `null` and
 * the scene says so.
 */
export function moduleFor(segment: string | undefined): OemModule | null {
  if (!segment) return null;
  const id = decodeURIComponent(segment);
  const direct = MODULE_BY_ID[id as ModuleId];
  if (direct) return direct;
  const viaStage = STAGE_TO_MODULE[id as LegacyStage];
  return viaStage ? MODULE_BY_ID[viaStage] : null;
}

/** Where the module sits in the ten, and what is either side of it. */
export interface ModuleNeighbours {
  readonly index: number;
  readonly prev: OemModule;
  readonly next: OemModule;
}

/**
 * The previous and next module, wrapping — demo line 3011's
 * `MODULES[(i+9)%10]` and `MODULES[(i+1)%10]`.
 */
export function neighbours(module: OemModule): ModuleNeighbours {
  const index = MODULES.findIndex((m) => m.id === module.id);
  const n = MODULES.length;
  return {
    index,
    prev: MODULES[(index + n - 1) % n],
    next: MODULES[(index + 1) % n],
  };
}

/** A system row under "Systems feeding this module". */
export interface SystemRow {
  /** The name as the module names it — the key the join is on. */
  readonly name: string;
  /** The connector's dimmed qualifier, when the name matched one. */
  readonly subtitle?: string;
  readonly icon?: string;
  readonly status?: OemConnector['status'];
  readonly tone?: OemConnector['tone'];
  /** True when the name matched a connector and the row can be opened. */
  readonly linked: boolean;
}

/**
 * Join `OemModule.systems` onto the connector roster — demo line 3015.
 *
 * A name with no connector still renders, as a plain label. The demo did the
 * same (`c ? … : '<div class="sys"><b>…</b></div>'`), and it is the right
 * behaviour: the module genuinely reads that system, the roster simply has no
 * card for it, and dropping the row would understate what feeds the module.
 */
export function systemRows(module: OemModule): readonly SystemRow[] {
  return module.systems.map((name): SystemRow => {
    const conn = CONNECTORS.find((c: OemConnector) => c.name === name);
    if (!conn) return { name, linked: false };
    return {
      name: conn.name,
      subtitle: conn.subtitle,
      icon: conn.icon,
      status: conn.status,
      tone: conn.tone,
      linked: true,
    };
  });
}

/** An agent card in "Agents in this module". */
export interface ModuleAgent {
  readonly name: string;
  readonly description: string;
  readonly level: OemAgent['level'];
  /** `'L2 Automated'` / `'L1 Assisted'`, as the demo composed it. */
  readonly levelLabel: string;
  /** The module-column tint, `AGENT_COLOURS`, falling back to the accent. */
  readonly colour: string;
  readonly lastRun: OemAgent['lastRun'];
}

/** The module's agents — demo line 3014's `AG.filter(a => m.agents.includes(a[0]))`. */
export function moduleAgents(module: OemModule): readonly ModuleAgent[] {
  return module.agents
    .map((name) => AGENT_BY_NAME[name])
    .filter((a): a is OemAgent => a !== undefined)
    .map(
      (a): ModuleAgent => ({
        name: a.name,
        description: a.description,
        level: a.level,
        levelLabel: `${a.level} ${a.level === 'L2' ? 'Automated' : 'Assisted'}`,
        colour: AGENT_COLOURS[a.name] ?? 'var(--accent)',
        lastRun: a.lastRun,
      }),
    );
}

/** A capability card in the module's lineage strip. */
export interface ModuleCapability {
  readonly id: string;
  readonly n: string;
  readonly name: string;
  readonly then: string;
  readonly now: string;
  readonly coverage: string;
  readonly coverageKey: Area['cov'];
  readonly agent: string;
  readonly links: Area['links'];
  readonly stat: Area['stat'];
}

/**
 * The capabilities this module absorbed — demo line 3014's
 * `AREAS.filter(a => m.caps.includes(a.id))`.
 *
 * In `AREAS` order, not `caps` order, which is what the demo's filter gave and
 * is the order the original wheel numbered them in.
 */
export function moduleCapabilities(module: OemModule): readonly ModuleCapability[] {
  return AREAS.filter((a: Area) => module.caps.includes(a.id)).map(
    (a: Area): ModuleCapability => ({
      id: a.id,
      n: String(a.n).padStart(2, '0'),
      name: a.name,
      then: a.then,
      now: a.now,
      coverage: AREA_COVERAGE_LABEL[a.cov],
      coverageKey: a.cov,
      agent: a.agent,
      links: a.links,
      stat: a.stat,
    }),
  );
}

/** A step tile in "Inside this module". */
export interface StepTile {
  readonly id: string;
  readonly name: string;
  readonly tag: string;
  /** The route, or `null` for the one tile that is not a workflow step. */
  readonly to: string;
}

/**
 * The module's step tiles — demo `wfTiles`, line 3689.
 *
 * `orchestrate` carries one extra tile the demo added by hand: the
 * case-to-closure trace, which is a whole-model page rather than a step of
 * that module. It is kept, and it is the only module with an extra.
 */
export function stepTiles(module: OemModule): readonly StepTile[] {
  const steps = workflowStepsForModule(module.id).map(
    (s: WorkflowStep): StepTile => ({
      id: s.id,
      name: s.name,
      tag: s.tag,
      to: `/workflows/steps/${s.id}`,
    }),
  );

  if (module.id !== 'orchestrate') return steps;
  return [
    ...steps,
    {
      id: 'case-trace',
      name: 'Case-to-closure trace',
      tag: 'one case end to end through all ten modules',
      to: '/workflows/case-trace',
    },
  ];
}

/** A row of "What this module does". */
export interface ModuleItemRow {
  /** Stable key — `what` is unique inside a module. */
  readonly key: string;
  readonly what: string;
  readonly where: string;
  /** The live figure, already resolved to a string. */
  readonly figure: string;
  readonly statusLabel: string;
  readonly statusTone: string;
  readonly nav: OemModule['items'][number]['nav'];
}

/** Status pill copy and tone for a module item — the demo's `ST` table. */
export const statusToken = (status: keyof typeof MODULE_STATUS_TOKENS) =>
  MODULE_STATUS_TOKENS[status];

/** `'01'` … `'10'`. */
export const moduleNumber = (module: OemModule): string => String(module.n).padStart(2, '0');

/** `'Module 4 of 10 in the Asset360 workflow'` — demo line 3017. */
export const modulePosition = (module: OemModule): string =>
  `Module ${module.n} of ${MODULES.length} in the Asset360 workflow`;
